import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

const EMPTY = {
  tipo: "fisica", nombre: "", tipo_identificacion: "cedula", identificacion: "",
  telefono: "", whatsapp: "", email: "",
  direccion: { provincia: "", canton: "", distrito: "", detalle: "" },
  ocupacion: "", fecha_ingreso: "", notas: "", activo: true,
};

const inputCls = "h-11 bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]";
const labelCls = "text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold";

export default function ClienteDialog({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (editing) {
        setForm({
          ...EMPTY,
          ...editing,
          direccion: { ...EMPTY.direccion, ...(editing.direccion || {}) },
        });
      } else {
        setForm(EMPTY);
      }
    }
  }, [open, editing]);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setD = (k, v) => setForm((s) => ({ ...s, direccion: { ...s.direccion, [k]: v } }));

  // Auto-adjust default tipo_identificacion when tipo changes
  const onTipoChange = (v) => {
    setForm((s) => ({
      ...s,
      tipo: v,
      tipo_identificacion: v === "juridica" ? "cedula_juridica" : (s.tipo_identificacion === "cedula_juridica" ? "cedula" : s.tipo_identificacion),
    }));
  };

  const save = async () => {
    if (!form.nombre.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/clientes/${editing.id}`, form);
        toast.success("Cliente actualizado");
      } else {
        await api.post("/clientes", form);
        toast.success("Cliente registrado");
      }
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#FBF9F4] border-[#E5DFD3]" data-testid="cliente-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1A0810]">
            {editing ? "Editar cliente" : "Registrar nuevo cliente"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          {/* Tipo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className={labelCls}>Tipo de cliente</Label>
              <Select value={form.tipo} onValueChange={onTipoChange}>
                <SelectTrigger data-testid="cliente-form-tipo" className={`${inputCls} mt-2`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fisica">Persona física</SelectItem>
                  <SelectItem value="juridica">Persona jurídica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={labelCls}>Estado</Label>
              <div className="mt-2 h-11 flex items-center gap-3 px-3 rounded-md bg-white border border-[#E5DFD3]">
                <Switch data-testid="cliente-form-activo" checked={form.activo} onCheckedChange={(v) => setF("activo", v)} />
                <span className="text-sm text-[#1A0810]">{form.activo ? "Activo" : "Inactivo"}</span>
              </div>
            </div>
          </div>

          {/* Nombre */}
          <div>
            <Label className={labelCls}>{form.tipo === "juridica" ? "Razón social *" : "Nombre completo *"}</Label>
            <Input
              data-testid="cliente-form-nombre"
              value={form.nombre}
              onChange={(e) => setF("nombre", e.target.value)}
              className={`${inputCls} mt-2`}
              placeholder={form.tipo === "juridica" ? "Empresa Rojas S.A." : "Andrea Solano Rojas"}
            />
          </div>

          {/* Identificación */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className={labelCls}>Tipo de identificación</Label>
              <Select value={form.tipo_identificacion} onValueChange={(v) => setF("tipo_identificacion", v)}>
                <SelectTrigger data-testid="cliente-form-tipo-id" className={`${inputCls} mt-2`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cedula">Cédula</SelectItem>
                  <SelectItem value="dimex">DIMEX</SelectItem>
                  <SelectItem value="cedula_juridica">Cédula jurídica</SelectItem>
                  <SelectItem value="pasaporte">Pasaporte</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className={labelCls}>Número de identificación</Label>
              <Input
                data-testid="cliente-form-identificacion"
                value={form.identificacion}
                onChange={(e) => setF("identificacion", e.target.value)}
                className={`${inputCls} mt-2`}
                placeholder={form.tipo === "juridica" ? "3-101-123456" : "1-1234-5678"}
              />
            </div>
          </div>

          {/* Contacto */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className={labelCls}>Teléfono</Label>
              <Input data-testid="cliente-form-telefono" value={form.telefono} onChange={(e) => setF("telefono", e.target.value)} className={`${inputCls} mt-2`} placeholder="+506 2222-3333" />
            </div>
            <div>
              <Label className={labelCls}>WhatsApp</Label>
              <Input data-testid="cliente-form-whatsapp" value={form.whatsapp} onChange={(e) => setF("whatsapp", e.target.value)} className={`${inputCls} mt-2`} placeholder="+506 8888-7777" />
            </div>
            <div className="md:col-span-2">
              <Label className={labelCls}>Correo electrónico</Label>
              <Input data-testid="cliente-form-email" type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} className={`${inputCls} mt-2`} placeholder="cliente@correo.cr" />
            </div>
          </div>

          {/* Dirección */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold mb-3">Dirección</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className={labelCls}>Provincia</Label>
                <Input data-testid="cliente-form-provincia" value={form.direccion.provincia} onChange={(e) => setD("provincia", e.target.value)} className={`${inputCls} mt-2`} placeholder="San José" />
              </div>
              <div>
                <Label className={labelCls}>Cantón</Label>
                <Input data-testid="cliente-form-canton" value={form.direccion.canton} onChange={(e) => setD("canton", e.target.value)} className={`${inputCls} mt-2`} placeholder="Escazú" />
              </div>
              <div>
                <Label className={labelCls}>Distrito</Label>
                <Input data-testid="cliente-form-distrito" value={form.direccion.distrito} onChange={(e) => setD("distrito", e.target.value)} className={`${inputCls} mt-2`} placeholder="San Rafael" />
              </div>
            </div>
            <div className="mt-4">
              <Label className={labelCls}>Dirección exacta</Label>
              <Textarea
                data-testid="cliente-form-direccion-detalle"
                value={form.direccion.detalle} onChange={(e) => setD("detalle", e.target.value)}
                className="mt-2 min-h-[64px] bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]"
                placeholder="Del McDonald's 200m sur, casa color blanco"
              />
            </div>
          </div>

          {/* Extras */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className={labelCls}>{form.tipo === "juridica" ? "Actividad económica" : "Ocupación"}</Label>
              <Input data-testid="cliente-form-ocupacion" value={form.ocupacion} onChange={(e) => setF("ocupacion", e.target.value)} className={`${inputCls} mt-2`} placeholder={form.tipo === "juridica" ? "Comercio" : "Ingeniera"} />
            </div>
            <div>
              <Label className={labelCls}>Fecha de ingreso al bufete</Label>
              <Input data-testid="cliente-form-fecha-ingreso" type="date" value={form.fecha_ingreso} onChange={(e) => setF("fecha_ingreso", e.target.value)} className={`${inputCls} mt-2`} />
            </div>
          </div>
          <div>
            <Label className={labelCls}>Notas internas</Label>
            <Textarea
              data-testid="cliente-form-notas"
              value={form.notas} onChange={(e) => setF("notas", e.target.value)}
              className="mt-2 min-h-[72px] bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]"
              placeholder="Información relevante, contactos alternos, preferencias del cliente..."
            />
          </div>
        </div>

        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="cliente-save-button" className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Guardar cambios" : "Registrar cliente"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
